import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage, useUrlParams } from '@app/hooks';
import {
    handleGetAppsData,
    resetState,
    setOrderByDirection,
    setOrderByProperty,
    getModalSwitch,
    resetScroll,
    gridCheckAll
} from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    DATE_FORMAT,
    passlyFormatDate,
    dateIsValidForDisplay,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    hasPermission
} from '@app/helpers';
import { ITableHead, IListPageBarEnabled, IActiveFilter, IFabMenu, FilterEnum, IDotMenu, OrderType, Order } from '@app/models';
import { IAppLibrary } from '@app/models';
import { hasOrgPermission } from '@app/helpers/permissions';
import { ManageSystem, ReadDirectory, ManageAccount, Audit } from '@app/store/constants';
import { basicService, appLibraryService } from '@app/services';
import { FabMenu, PasslyTableCell, IModalData, ModalComponent, PasslyTable, AvatarComponent } from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import AddApplicationListDialog from './dialogs/addApplicationList.dialog';

interface IAppsTableData {
    checked: boolean;
    displayName: string;
    users: Number;
    policyName: string;
    lastLaunched: Date;
    isSystemApplication: boolean;
    isOffice365App: boolean;
    iconUrl: string;
    id: string;
    cantEdit: boolean;
    cantDelete: boolean;
}

enum TableHeadEnum {
    image = 'image',
    displayName = 'displayName',
    users = 'users',
    policyName = 'policyName',
    lastLaunched = 'lastLaunched'
}

const AppLibraryPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.ApplicationLibrary);
    const userInfiniteScroll = infiniteScrollItemsFetchLimit;
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const { name: name } = useUrlParams<{ name: string }>();

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IAppsTableData[]>([]);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const [noPermsToView, setNoPermsToView] = useState(false);

    const apps = useSelector((state: any) => state.appLibrary.apps);
    const appsCount = useSelector((state: any) => state.appLibrary.count);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: true
    };

    const editRow = (e: any) => {
        basicService.redirect('/sso/library/details/' + e.currentTarget.dataset.id);
    };

    const getComplexAvatarCell = (row: any) => {
        return (
            <PasslyTableCell align='center'>
                {hasOrgPermission([ManageSystem]) ? !row.isSystemApplication ? <AvatarComponent overrideUrl={row.iconUrl} size='small' /> : '' : ''}
            </PasslyTableCell>
        );
    };
    const getComplexLastLaunchedCell = (row: any) => {
        return (
            <PasslyTableCell>
                {dateIsValidForDisplay(row.lastLaunched) ? passlyFormatDate(row.lastLaunched, DATE_FORMAT) : translation('DATE_NEVER')}
            </PasslyTableCell>
        );
    };

    // Only sortable on display name for apps, handled in return() function
    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.image,
            label: '',
            type: 'getComplexCell',
            width: '80px',
            getComplexCell: getComplexAvatarCell,
            hideSorting: true,
        },
        {
            id: TableHeadEnum.displayName,
            label: 'APP_NAME',
            type: 'link',
            onClick: editRow,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
        },
        {
            id: TableHeadEnum.users,
            label: 'APP_USERS',
            hideSorting: true
        },
        {
            id: TableHeadEnum.policyName,
            label: 'APP_AUTH_POLICY',
            hideSorting: true
        },
        {
            id: TableHeadEnum.lastLaunched,
            label: 'APP_LAST_LAUNCHED',
            type: 'getComplexCell',
            getComplexCell: getComplexLastLaunchedCell,
            hideSorting: true
        }
    ];

    const menu: IFabMenu = {
        name: 'ADD_APP',
        actions: [
            {
                name: 'NEW_APP',
                method: () => addNewApplication(''),
                iconElement: <i className='icon icon-sso-manager' />
            }
        ]
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        if (name && name !== undefined && name !== '') {
            addNewApplication(name);
        }
    }, [name]);

    useEffect(() => {
        setNoPermsToView(!hasOrgPermission([ManageSystem, ReadDirectory, ManageAccount, Audit]));
    }, [organizationFilter]);

    useEffect(() => {
        if (!loadingData && apps) {
            let newRows = apps.map((app: IAppLibrary) => {
                const data: IAppsTableData = {
                    checked: false,
                    displayName: app.DisplayName,
                    users: app.Users,
                    policyName: app.PolicyName,
                    lastLaunched: app.LastLaunched,
                    isSystemApplication: app.IsSystemApplication,
                    iconUrl: app.IconUrl,
                    id: app.Id,
                    isOffice365App: app.IsOffice365App,
                    cantEdit: app.IsSystemApplication || !hasOrgPermission(['ManageSystem']),
                    cantDelete: app.IsSystemApplication || !hasOrgPermission(['ManageSystem'])
                };
                return data;
            });

            if (apps.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, apps]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetAppsData(orderBy, order, userInfiniteScroll, skipCount, organizationFilter, isReset, activeFilters));
    };

    const addNewApplication = (specificAppName: string) => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_APP'),
            modalSwitcher: modalSwitchState,
            size: 'lg',
            form: <AddApplicationListDialog specificAppName={specificAppName} />
        });
    };

    const deleteRow = (e: any) => {
        let isOffice = e.currentTarget.dataset.isoffice;
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        if (isOffice === 'true') {
            deleteAppsAreYouSureOffice365(ids);
        } else {
            deleteAppsAreYouSure(ids);
        }
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];
        let atLeast1O365: boolean = false;

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);

                if (row.isOffice365App) {
                    atLeast1O365 = true;
                }
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        if (atLeast1O365) {
            deleteAppsAreYouSureOffice365Multiple(ids);
        } else {
            deleteAppsAreYouSure(ids);
        }
    }, [rows]);

    const deleteAppsAreYouSure = (ids: any) => {
        deleteConfirmationBox(translation('DELETE_APP_BODY'), ids);
    };

    const deleteAppsAreYouSureOffice365 = (ids: any) => {
        deleteConfirmationBox(translation('DELETE_APP_BODY_OFFICE365'), ids);
    };

    const deleteAppsAreYouSureOffice365Multiple = (ids: any) => {
        deleteConfirmationBox(translation('DELETE_APP_BODY_OFFICE365_MULTIPLE'), ids);
    };

    const deleteConfirmationBox = (bodyText: string, ids: any) => {
        confirm(bodyText, translation('DELETE'), translation('CANCEL'), translation('DELETE_APP_HEADER'), false, { isDeleteAction: true }).then(
            () => {
                deleteApps(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteApps = (ids: string[]) => {
        if (ids.length > 0) {
            let apps: any = {
                Applications: ids
            };

            appLibraryService.deleteApps(apps, genericErrorToast, handleDeleteSuccess);
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
            dispatch(gridCheckAll(false));
        }
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    return (
        <>
            <ModalComponent {...modalData} />

            {!loadingData && hasOrgPermission([ManageSystem]) && <FabMenu menu={menu} />}

            {noPermsToView ? (
                <div className='table--empty'>{translation('NO_ORG_APP_PERMISSIONS')}</div>
            ) : (
                <PasslyTable
                    rows={rows}
                    setRows={setRows}
                    order={order}
                    orderBy={orderBy}
                    handleOrderChange={createSortHandler}
                    headerItems={headCells}
                    rowsCount={appsCount}
                    onFetch={fetchMoreData}
                    withCheckboxes={hasPermission([ManageSystem])}
                    actionMenuItems={actionMenuItems}
                    emptyListHelper={translation('EMPTY_APP_LIST')}
                    loadingData={loadingData}
                    editRow={editRow}
                    deleteRow={deleteRow}
                />
            )}
        </>
    );
};

export default AppLibraryPage;
